<template>
    <StepShell :embedded="embedded" gap="tight">
        <header class="reveal">
            <ArchetypeDot :letters="result.letters" class="medallion" />
            <div>
                <p class="lead-in">{{ t("onboarding.calendarFirst.perspectiveResultStep.leadIn") }}</p>
                <h2 class="archetype">{{ archetype }}</h2>
            </div>
            <!-- Use answer-derived copy instead of the typology library's third-person description to
                 preserve Nadia's voice and explain the dimension chart.
                 -->
            <p class="blurb">{{ leanCopy }}</p>
        </header>

        <!-- Show dimension strengths because the next reading uses these scores, not just the four-letter
             type.
             -->
        <section class="dimensions">
            <DimensionBar
                v-for="dimension in result.dimensions"
                :key="dimension.code"
                :code="dimension.code"
                :binned-score="dimension.binned_score"
                :left-label="DIMENSION_LABELS[dimension.code].left"
                :right-label="DIMENSION_LABELS[dimension.code].right" />
        </section>

        <p class="handoff">{{ handoffCopy }}</p>

        <StepActions flush>
            <BaseButton v-if="retakeable" theme="tertiary" @click="emit('retake')">{{ t("onboarding.calendarFirst.perspectiveResultStep.retake") }}</BaseButton>
            <BaseButton theme="primary" @click="emit('continue')">{{ ctaLabel }}</BaseButton>
        </StepActions>
    </StepShell>
</template>

<script setup lang="ts">
import BaseButton from "~vue/components/BaseButton.vue";
import type { PerspectiveResult } from "~vue/pages/perspectiveTypes";
import { DIMENSION_LABELS, PERSPECTIVE_TYPES } from "~vue/pages/perspectiveTypes";
import ArchetypeDot from "~vue/Perspective/ArchetypeDot.vue";
import DimensionBar from "~vue/UserProfile/DimensionBar.vue";
import { computed } from "vue";
import { useI18n } from "vue-i18n";

import StepActions from "./StepActions.vue";
import StepShell from "./StepShell.vue";

/**
 * Names the archetype the assessment produced, between answering and the second
 * read of the calendar.
 *
 * It is a screen of its own rather than a badge on the readings for the reason
 * `SharpenedReadingsStep` records: a medallion above those cards would reframe
 * them as a personality-test result. Separating the two lets this screen be the
 * result and that one stay the readings — and skipping the reveal entirely, as
 *
 * Copy comes from `pages/perspectiveTypes.ts`, the same source the standard
 * results page uses, so the archetype cannot be described two ways.
 */

const {
    embedded = false,
    result,
    calendarConnected = true,
    retakeable = false,
} = defineProps<{
    embedded?: boolean;
    result: PerspectiveResult;
    /** False when there is no calendar to re-read, so the handoff cannot promise a second look at it. */
    calendarConnected?: boolean;
    /** True when the user reopened a finished assessment, where this screen is the only way back to it. */
    retakeable?: boolean;
}>();

const emit = defineEmits<{ continue: []; retake: [] }>();

const { t } = useI18n();

const typeData = computed(() => PERSPECTIVE_TYPES[result.letters] ?? null);

const leanCopy = computed(() => {
    const leans = [...result.dimensions]
        .filter((dimension) => dimension.binned_score !== 0)
        .sort((a, b) => Math.abs(b.binned_score) - Math.abs(a.binned_score))
        .map((dimension) => (dimension.binned_score > 0 ? DIMENSION_LABELS[dimension.code].right : DIMENSION_LABELS[dimension.code].left).toLowerCase());
    if (leans.length === 0) {
        return t("onboarding.calendarFirst.perspectiveResultStep.lean.centered");
    }
    const [a, b, c, d] = leans;
    const named = t("onboarding.calendarFirst.perspectiveResultStep.lean.list", { count: leans.length, a, b, c, d }, leans.length);
    return t("onboarding.calendarFirst.perspectiveResultStep.lean.named", { leans: named });
});
const archetype = computed(() => typeData.value?.title ?? result.archetype);

/* Names what the type is for here rather than leaving the user to infer it: this
   screen sits mid-flow, and the next one is the payoff it is setting up. */
/*
 * Without a connected calendar, this flow cannot produce improved calendar insights, so the CTA must not
 * promise them.
 */
const ctaLabel = computed(() => (calendarConnected ? t("onboarding.calendarFirst.perspectiveResultStep.improvedInsights") : t("onboarding.calendarFirst.actions.continue")));

const handoffCopy = computed(() =>
    calendarConnected ? t("onboarding.calendarFirst.perspectiveResultStep.handoff.calendar") : t("onboarding.calendarFirst.perspectiveResultStep.handoff.noCalendar"),
);
</script>

<style scoped>
@reference "../../../css/tailwind.css";

@layer components {
    .reveal {
        @apply flex flex-col items-center gap-5;
    }

    .medallion {
        @apply size-[66px];
    }

    .lead-in {
        @apply body-small text-center text-ink-muted;
    }

    .archetype {
        @apply display-serif mt-1 text-center text-ink;
    }

    .blurb {
        @apply body text-ink-muted;
    }

    /*
     * Reuse the Perspectives results page's dimension bars to keep chart styling and colours consistent
     * across both views.
     */
    .dimensions {
        @apply flex w-full flex-col gap-6;
    }

    .handoff {
        @apply body text-ink-muted;
    }
}
</style>
